/**
 * Shape drawing tools — rectangle (and, as this grows, circle / ellipse /
 * triangle / rotated rect).
 *
 * WHY THIS FILE EXISTS: `rect`, `circle`, `polygon`, `arc` and `text` were
 * listed in `OVERLAY_GROUPS` but never registered as overlay *templates*. Those
 * names are klinecharts *figures* (primitives you return from
 * `createPointFigures`), not overlays. klinecharts fails silently on an unknown
 * overlay name — `OverlayStore.addInstances` gets `null` from
 * `getOverlayInnerClass` and returns without warning — so selecting Rectangle
 * did nothing at all. See docs/superpowers/specs/2026-07-27-drawing-tools-catalog-design.md (F1).
 *
 * Fill comes from `OverlayStyleOverride.fillColor` / `fillOpacity`, mapped to
 * klinecharts' `rect`/`polygon`/`circle` style keys by
 * `ChartManager.overlayStyleToKline`. The figure builder here only sets the
 * stroke, so an unstyled shape renders as an outline rather than a solid blob.
 */
import { registerOverlay } from 'klinecharts'
import { isNum, rectFigure, type Coord, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

/** The subset of a resolved overlay style the shape builders read. */
export interface ShapeStyle {
  color?: string
  size?: number
}

/**
 * The closed shapes registered below that draw a fill, i.e. the tools the
 * settings drawer offers a Background control for. Keep in step with the
 * `define(...)` calls that pass a fill-aware builder.
 */
export const FILLABLE_SHAPES: ReadonlySet<string> = new Set([
  'rect',
  'circle',
  'ellipse',
  'triangle',
  'polygon',
])

/** Fill opacity when a shape has a `fillColor` but no opacity of its own. */
export const DEFAULT_FILL_OPACITY = 0.2

/**
 * Rectangle figures from two opposite corners.
 *
 * Returns `[]` until both corners exist (klinecharts calls this mid-draw with a
 * partial point list) and when either coordinate is non-finite — drawing at NaN
 * silently corrupts the canvas rather than erroring.
 */
export function buildRectFigures(
  coordinates: Coord[],
  style: ShapeStyle,
  fill?: string,
): OverlayFigure[] {
  if (coordinates.length < 2) return []
  const [a, b] = coordinates
  if (!isNum(a?.x) || !isNum(a?.y) || !isNum(b?.x) || !isNum(b?.y)) return []
  return [
    rectFigure(
      a.x,
      a.y,
      b.x,
      b.y,
      {
        // ALWAYS explicit. klinecharts defaults a `rect` figure to 'fill',
        // which paints a solid block in its default colour — a new rectangle
        // must be an outline, with fill strictly opt-in. An empty fill is a
        // Background that was turned off (see overlayStyleToKline).
        style: fill ? 'stroke_fill' : 'stroke',
        ...(fill ? { color: fill } : {}),
        ...(style.color !== undefined ? { borderColor: style.color } : {}),
        ...(style.size !== undefined ? { borderSize: style.size } : {}),
      },
      true,
    ),
  ]
}

/** Shared stroke/fill style block, explicit about fill so klinecharts' default
 *  `style: 'fill'` can never sneak a solid block onto a new shape. */
function shapeStyles(style: ShapeStyle, fill?: string): Record<string, unknown> {
  return {
    style: fill ? 'stroke_fill' : 'stroke',
    ...(fill ? { color: fill } : {}),
    ...(style.color !== undefined ? { borderColor: style.color } : {}),
    ...(style.size !== undefined ? { borderSize: style.size } : {}),
  }
}

const allFinite = (pts: Coord[]): boolean => pts.every((p) => isNum(p?.x) && isNum(p?.y))

/** Circle from a centre point and a radius handle (the second click). */
export function buildCircleFigures(
  coordinates: Coord[],
  style: ShapeStyle,
  fill?: string,
): OverlayFigure[] {
  if (coordinates.length < 2) return []
  const [c, edge] = coordinates
  if (!allFinite([c, edge])) return []
  const r = Math.hypot(edge.x - c.x, edge.y - c.y)
  return [
    {
      type: 'circle',
      ignoreEvent: false,
      attrs: { x: c.x, y: c.y, r },
      styles: shapeStyles(style, fill),
    },
  ]
}

/** Vertices used to approximate an ellipse. 48 reads as smooth at any
 *  realistic on-chart size without bloating the persisted figure. */
const ELLIPSE_SEGMENTS = 48

/**
 * Ellipse inscribed in the bounding box of two opposite corners.
 *
 * klinecharts ships no ellipse figure (its registry is circle / line / polygon /
 * rect / text / rectText / arc), so this emits a closed polygon sampled on the
 * ellipse. Corner order is normalised, matching `rectFigure`.
 */
export function buildEllipseFigures(
  coordinates: Coord[],
  style: ShapeStyle,
  fill?: string,
): OverlayFigure[] {
  if (coordinates.length < 2) return []
  const [a, b] = coordinates
  if (!allFinite([a, b])) return []
  const cx = (a.x + b.x) / 2
  const cy = (a.y + b.y) / 2
  const rx = Math.abs(b.x - a.x) / 2
  const ry = Math.abs(b.y - a.y) / 2
  const coords: Coord[] = []
  for (let i = 0; i < ELLIPSE_SEGMENTS; i += 1) {
    const t = (i / ELLIPSE_SEGMENTS) * Math.PI * 2
    coords.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) })
  }
  return [
    {
      type: 'polygon',
      ignoreEvent: false,
      attrs: { coordinates: coords },
      styles: shapeStyles(style, fill),
    },
  ]
}

/**
 * Closed polygon through every placed point — the shared shape for Triangle
 * (3 points), Rotated Rectangle (4) and free Polygon (N). klinecharts closes a
 * `polygon` figure itself, so the first point is not repeated.
 */
export function buildPolygonFigures(
  coordinates: Coord[],
  style: ShapeStyle,
  fill?: string,
): OverlayFigure[] {
  if (coordinates.length < 3 || !allFinite(coordinates)) return []
  return [
    {
      type: 'polygon',
      ignoreEvent: false,
      attrs: { coordinates },
      styles: shapeStyles(style, fill),
    },
  ]
}

/** Open path through every placed point (Polyline / Path). Never filled. */
export function buildPolylineFigures(coordinates: Coord[], style: ShapeStyle): OverlayFigure[] {
  if (coordinates.length < 2 || !allFinite(coordinates)) return []
  return [
    {
      type: 'line',
      ignoreEvent: false,
      attrs: { coordinates },
      styles: {
        ...(style.color !== undefined ? { color: style.color } : {}),
        ...(style.size !== undefined ? { size: style.size } : {}),
      },
    },
  ]
}

/** Upper bound on vertices for the free-form tools (polygon / polyline). The
 *  user normally ends the shape with a double-click well before this. */
const MAX_FREE_POINTS = 30

/**
 * Register the shape overlay templates. Idempotent — `EdgeChart` calls this at
 * module load and every ChartManager shares the global klinecharts registry.
 */
export const registerShapeOverlays = registerOnce(() => {
  // `styles.rect.color` is populated by ChartManager.overlayStyleToKline ONLY
  // when the drawing carries a `fillColor`. Absent or '' (Background turned
  // off) → outline-only.
  const read = (overlay: unknown) => {
    const s = (overlay as { styles?: { line?: ShapeStyle; rect?: { color?: string } } })?.styles
    return { stroke: s?.line ?? {}, fill: s?.rect?.color }
  }

  const define = (
    name: string,
    totalStep: number,
    build: (c: Coord[], s: ShapeStyle, fill?: string) => OverlayFigure[],
  ) =>
    registerOverlay({
      name,
      totalStep,
      ...DEFAULT_FIGURES,
      createPointFigures: ({ coordinates, overlay }) => {
        const { stroke, fill } = read(overlay)
        return build(coordinates as Coord[], stroke, fill)
      },
    } as OverlayTemplateParam)

  // `totalStep` is clicks + 1. klinecharts advances one step per
  // `mouseClickEvent`, so these are all click-then-click. (Click-and-drag is
  // deliberately deferred — it is fiddly on touch.)
  define('rect', 3, buildRectFigures)
  define('circle', 3, buildCircleFigures)
  define('ellipse', 3, buildEllipseFigures)
  define('triangle', 4, buildPolygonFigures)

  // Variable-point tools: the user ends the shape with a DOUBLE-CLICK, which
  // klinecharts handles via `forceComplete` on `mouseDoubleClickEvent`. The
  // step cap is therefore an upper bound, not the expected point count.
  define('polygon', MAX_FREE_POINTS + 1, buildPolygonFigures)
  registerOverlay({
    name: 'polyline',
    totalStep: MAX_FREE_POINTS + 1,
    ...DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay }) =>
      buildPolylineFigures(coordinates as Coord[], read(overlay).stroke),
  } as OverlayTemplateParam)
})
